<template>
  <section
    v-bind="$attrs"
    data-el="Fraction"
    :data-fraction-id="id"
    :data-current="current"
    :data-limit="limit"
    :data-fraction-thresholds="JSON.stringify(thresholds)"
    :data-fraction-is-danger="isDanger ? '1' : '0'"
    :data-fraction-is-warning="isWarning ? '1' : '0'"
    :data-fraction-is-limited="isLimited ? '1' : '0'"
    :data-fraction-loading="loading ? '1' : '0'"
  >
    <div
      class="mb-1 flex justify-between"
      :class="{
        'text-cyan-600': !isLimited,
        'text-orange-600': isLimited && !loading && !isDanger && isWarning,
        'text-red-600': isLimited && !loading && isDanger && !isWarning,
      }"
    >
      <span
        class="text-sm font-normal tracking-tight text-gray-800"
        :title="tooltip"
      >
        <NuxtLink class="hover:underline" v-if="link" :to="link">{{
          title
        }}</NuxtLink>
        <span v-else>{{ title }}</span>
      </span>
      <span class="text-sm font-medium">
        <span v-if="isInfinite">
          <Icon icon="infinite" />
        </span>
        <span v-else> {{ percent }}% </span>
      </span>
    </div>
    <div class="h-2.5 w-full rounded-full bg-gray-200">
      <div
        class="h-2.5 rounded-full"
        :class="{
          'bg-cyan-600': !isLimited,
          'bg-orange-600': isLimited && !loading && !isDanger && isWarning,
          'bg-red-600': isLimited && !loading && isDanger && !isWarning,
        }"
        :style="`width: ${percent}%`"
      />
    </div>
  </section>
</template>
<script setup lang="ts">
import type { GetStatisticsFractionFractionTypeEnum } from '@mailslurp/admin-sdk';

import { onMounted, ref, computed } from 'vue';
import { useStore3 } from '~/store/main';
import Icon from '~/components/form/Icon.vue';
import { getLogger } from '~/lib/services/getLogger';

const store3 = useStore3();
const log = getLogger('Fraction');
const props = withDefaults(
  defineProps<{
    showLimitDanger?: boolean;
    title: string;
    id: string;
    disabled?: string;
    alert: string;
    link?: string;
    fractionType: GetStatisticsFractionFractionTypeEnum;
    thresholds?: {
      danger: number;
      warning: number;
    };
  }>(),
  { link: '', disabled: '', thresholds: undefined, showLimitDanger: false },
);
const isDanger = computed(() => {
  if (props.thresholds?.danger) {
    return percent.value >= props.thresholds.danger / 100;
  } else {
    return props.showLimitDanger;
  }
});
const isWarning = computed(() => {
  if (props.thresholds?.warning) {
    return percent.value >= props.thresholds.warning / 100;
  }
  return false;
});

const current = ref<number>(0);
const limit = ref<number>(0);
const loading = ref(true);
const description = ref<string>('');
const emit = defineEmits<{
  (e: 'limited'): void;
  (e: 'loaded'): void;
}>();
const isInfinite = computed(() => {
  return limit.value === -1;
});
const percent = computed(() => {
  if (isInfinite.value || limit.value === 0) {
    return 0;
  }
  return Math.min(100, Math.floor((current.value / limit.value) * 100.0));
});

onMounted(async () => {
  if (props.fractionType) {
    loading.value = true;
    try {
      const fraction = await store3.getStatisticsFraction({
        fractionType: props.fractionType,
      });
      current.value = fraction.current;
      limit.value = fraction.limit;
      description.value = fraction.description;
    } catch (e) {
      console.error(e);
    } finally {
      loading.value = false;
    }
    if (isLimited.value) {
      log(`Fraction ${props.fractionType} ${props.id} is limited. `);
      emit('limited');
    }
    emit('loaded');
  }
});

const tooltip = computed<string>(() => {
  if (limit.value === 0) {
    return props.disabled;
  } else if (isLimited.value) {
    return props.alert;
  } else {
    return description.value;
  }
});

const isLimited = computed(() => {
  if (limit.value === -1 || current.value === -1) {
    return false;
  }
  return current.value > limit.value;
});
</script>
